import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface ArticleMeta {
  title: string;
  description: string;
  keywords: string;
  slug: string;
  readTime: string;
  badge: string;
  imageUrl: string;
  imageAlt: string;
}

interface RelatedArticle {
  title: string;
  slug: string;
}

interface ArticleLayoutProps {
  meta: ArticleMeta;
  relatedArticles: RelatedArticle[];
  children: ReactNode;
}

const SITE_URL = "https://pandabuy.lifestyle";

export default function ArticleLayout({ meta, relatedArticles, children }: ArticleLayoutProps) {
  const canonicalUrl = `${SITE_URL}/${meta.slug}`;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": meta.title,
    "description": meta.description,
    "datePublished": "2026-06-29",
    "dateModified": "2026-06-29",
    "author": { "@type": "Organization", "name": "PandabuySpreadsheet" },
    "publisher": { "@type": "Organization", "name": "PandabuySpreadsheet", "url": SITE_URL },
    "mainEntityOfPage": { "@type": "WebPage", "@id": canonicalUrl }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />
      <link rel="canonical" href={canonicalUrl} />

      <Navbar />

      <main>
        <section className="w-full pt-24 pb-10 md:pt-28 md:pb-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Link
                to="/"
                className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-1"></i>Back to PandabuySpreadsheet Hub
              </Link>
              <span className="text-foreground-300">/</span>
              <span className="text-xs text-foreground-500">{meta.badge}</span>
            </div>

            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-4">
              {meta.badge}
            </span>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {meta.title}
            </h1>

            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              {meta.description}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
                Updated June 2026
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center"></i>
                {meta.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                8,000+ readers
              </span>
            </div>
          </div>
        </section>

        <article className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={meta.imageUrl}
                alt={meta.imageAlt}
                className="w-full h-auto object-cover"
              />
            </div>

            {children}

            <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-xl border border-background-200/60 text-center">
              <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-2">
                Ready to Transform Your Fashion Shopping?
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                Explore the complete PandabuySpreadsheet database with 14 curated fashion categories and 1200+ hand-picked items.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-2.5 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Browse PandabuySpreadsheet Hub
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-2.5 rounded-lg text-sm font-semibold border border-primary-300 text-primary-600 hover:bg-primary-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Access Full Database
                </a>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/60">
              <p className="text-xs text-foreground-500 mb-3">Related Resources:</p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-background-100 text-foreground-700 hover:text-primary-600 border border-background-200/60 hover:border-primary-200/60 transition-all duration-200 whitespace-nowrap"
                >
                  <i className="ri-home-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  PandabuySpreadsheet Home
                </Link>
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/${ra.slug}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-background-100 text-foreground-700 hover:text-primary-600 border border-background-200/60 hover:border-primary-200/60 transition-all duration-200 whitespace-nowrap"
                  >
                    <i className="ri-article-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {ra.title}
                  </Link>
                ))}
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-background-100 text-foreground-700 hover:text-primary-600 border border-background-200/60 hover:border-primary-200/60 transition-all duration-200 whitespace-nowrap"
                >
                  <i className="ri-external-link-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  Pandabuy Sheet
                </a>
              </div>
            </div>
          </div>
        </article>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      </main>

      <Footer />
    </div>
  );
}